import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';

const DEFAULT_FAQS = [
  {
    id: 'faq-setup-time',
    question: 'How long does setup take?',
    answer: 'Usually 24–48 hours from the time you submit the form.',
  },
  {
    id: 'faq-payment',
    question: 'When do I get paid?',
    answer:
      'Payouts are processed every Monday via NEFT directly to the bank account linked to your librarian wallet. No holds, no surprises.',
  },
  {
    id: 'faq-setup-fee',
    question: 'Is there any setup fee?',
    answer:
      'No. Listing is completely free. We only earn when you do — via a small commission on successful online bookings. You keep 100% of your offline collections.',
  },
  {
    id: 'faq-existing-system',
    question: 'Can I use BookMyLibrary alongside my existing system?',
    answer: 'Yes. Most partners run both during the first month, then switch fully.',
  },
  {
    id: 'faq-multiple-branches',
    question: 'What if I have multiple branches?',
    answer: 'Each branch is listed as its own library. You manage all of them from one dashboard.',
  },
  {
    id: 'faq-data',
    question: 'How is student data handled?',
    answer: 'All data is stored securely in India. Only you and the student can access their records.',
  },
];

function FaqSection({ eyebrow, title, subtitle, items }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(27,54,93,0.08)';
  const accordionBg = isDark ? 'rgba(27,54,93,0.4)' : theme.palette.background.paper;

  return (
    <SectionContainer
      id="faq"
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
    >
      <RevealOnScroll>
        <Box sx={{ maxWidth: 840, mx: 'auto' }}>
          {items.map((faq, i) => (
            <Accordion
              key={faq.id || faq.question}
              expanded={expanded === `panel${i}`}
              onChange={handleChange(`panel${i}`)}
              disableGutters
              elevation={0}
              sx={{
                background: accordionBg,
                border: `1px solid ${borderColor}`,
                borderRadius: '12px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  border: `1px solid ${theme.palette.primary.main}40`,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreOutlined sx={{ color: theme.palette.primary.main }} />
                }
                aria-controls={`panel${i}-content`}
                id={`panel${i}-header`}
                sx={{ px: { xs: 2.5, md: 3 }, py: 0.5 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pb: 2.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </RevealOnScroll>
    </SectionContainer>
  );
}

FaqSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      question: PropTypes.string.isRequired,
      answer: PropTypes.string.isRequired,
    })
  ),
};

FaqSection.defaultProps = {
  eyebrow: 'Questions?',
  title: 'Frequently asked.',
  subtitle: "Can't find what you're looking for? Chat with us on WhatsApp.",
  items: DEFAULT_FAQS,
};

export default FaqSection;
